import { ThingEventSnapshot } from '@eagle/core-data-types';
import { Alert, Collapse, IconButton, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import chroma from 'chroma-js';
import { FC, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { FormatTimestamp, Icon, MiddleSpinner } from '../../components';
import { COLLAPSE_DEFAULT_TIME, T_ONE } from '../../constants';
import { useSearch } from '../../pages/list/use-search';
import { testid } from '../../util';
import { EntryDataBody, EventTableCategory, EventTableEventIcon } from './util';

interface LayoutDesktopProps {
  events: ThingEventSnapshot[];
  hideCategory: boolean;
  isLoading: boolean;
  paginationComponent?: JSX.Element;
  rowChildren?: (event: ThingEventSnapshot) => JSX.Element;
  tableHeight: string;
}

interface StyledTableContainerProps {
  tableHeight: string;
}

interface StyledTableBodyProps {
  isLoading: boolean;
}

const StyledTableBody = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'isLoading',
})<StyledTableBodyProps>(({ isLoading }) => ({
  filter: isLoading ? 'blur(1px)' : '',
  opacity: isLoading ? 0.66 : 1,
}));

const StyledCollapse = styled(Collapse)({
  '.MuiCollapse-wrapperInner': {
    display: 'flex',
  },
});

const StyledTableContainer = styled(TableContainer)<StyledTableContainerProps>(({ tableHeight }) => ({
  minHeight: tableHeight,
}));

const StyledTableRow = styled(TableRow)();
const StyledTableCell = styled(TableCell)();

const Row: FC<{ event: ThingEventSnapshot; hideCategory: boolean; rowChildren?: (event: ThingEventSnapshot) => JSX.Element }> = ({ event, hideCategory, rowChildren }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  return <>
    <StyledTableRow data-testid={testid`events-table-row-item-${event.eventId}`} sx={{ '& > *': { borderBottom: 'unset' } }}>
      <StyledTableCell sx={{ width: '5%', py: 0, pr: 0, pl: 1, verticalAlign: 'center' }} >
        <IconButton aria-label="expand row" data-testid="expand-button" size="small" onClick={() => setOpen(!open)}>
          {open ? <Icon data-testid="icon-chevron-up" icon="ChevronUp" /> : <Icon data-testid="icon-chevron-down" icon="ChevronDown" />}
        </IconButton>
      </StyledTableCell>
      <StyledTableCell component="th" scope="row" sx={{ verticalAlign: 'top' }}>
        <EventTableEventIcon event={event} />
      </StyledTableCell>
      {!hideCategory && <StyledTableCell sx={{ p: 0 }}> <EventTableCategory eventTypeId={event.eventTypeId} feature={event.feature} /> </StyledTableCell>}
      <StyledTableCell data-testid="event-date-time" sx={{ p: 0 }}> {<FormatTimestamp value={event.occurred} format="shortWithSeconds" />} </StyledTableCell>
    </StyledTableRow>
    <StyledTableRow sx={{ transition: `borderBottom ${COLLAPSE_DEFAULT_TIME}ms linear` }}>
      <StyledTableCell colSpan={6} sx={{ '&': { borderBottom: open ? `1px solid ${chroma(theme.palette.common.black).alpha(0.12).css()}` : 'none' }, py: 0, px: 1 }}>
        <StyledCollapse in={open} unmountOnExit>
          <Stack direction="column" width="100%">
            <EntryDataBody event={event} />
            {rowChildren && rowChildren(event)}
          </Stack>
        </StyledCollapse>
      </StyledTableCell>
    </StyledTableRow>
  </>;
};

export const EventsTableDesktopView: FC<LayoutDesktopProps> = ({ events, hideCategory, isLoading, paginationComponent, rowChildren, tableHeight }) => {
  const { t } = useTranslation(['common']);
  const { filters } = useSearch();

  if (!events || !events.length) {
    if (isLoading) {
      return (
        <Stack minHeight={tableHeight}>
          <MiddleSpinner sx={{ height: 'auto', pt: '140px' }} />
        </Stack>
      );
    }
    return (
      <Stack minHeight={tableHeight}>
        <Box m={3}>
          {filters.length
            ? <Alert severity="info">{t('common:component.events.thing-event.labels.table.filter-no-results')}</Alert>
            : <Alert severity="info">{t('common:component.events.thing-event.labels.table.filter-no-selection')}</Alert>
          }
        </Box>
      </Stack>
    );
  }

  return (
    <StyledTableContainer tableHeight={tableHeight}>
      <Table aria-label="collapsible table" data-testid="events-table" >
        <TableHead>
          <StyledTableRow>
            <StyledTableCell />
            <StyledTableCell sx={{ pr: 0, py: 1 }}>{t('common:component.events-table.labels.event-type', { count: T_ONE })}</StyledTableCell>
            {!hideCategory && <StyledTableCell sx={{ px: 0, py: 1 }}>{t('common:component.events.labels.category')}</StyledTableCell>}
            <StyledTableCell sx={{ px: 0, py: 1, width: '30%' }}>{t('common:terms.date-time')}</StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <StyledTableBody isLoading={isLoading}>
          {events.map((event) => <Row key={event.eventId} event={event} hideCategory={hideCategory} rowChildren={rowChildren} />)}
        </StyledTableBody>
      </Table>
      {paginationComponent
        ? <Box display="flex" justifyContent="center" marginY={1}>
          {paginationComponent}
        </Box>
        : null
      }
    </StyledTableContainer>
  );
};
